/* Al Basha Silver - instant product search (added by Claude).
   Searches the store's product list directly in the browser, with Arabic
   letter normalisation, so it works for Arabic and English words. */
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;
    this.searchTerm = '';
    this.isArabic = (document.documentElement.lang || 'ar').indexOf('ar') === 0;
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.input.addEventListener('focus', () => PredictiveSearch.loadIndex(), { once: true });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    event.preventDefault();
    const selected = this.querySelector('[aria-selected="true"] a');
    if (selected) {
      selected.click();
      return;
    }
    if (this.getQuery().length) this.getSearchResults(this.getQuery(), 30);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.closeResults(true);
    }
  }

  onFocus() {
    const q = this.getQuery();
    if (q.length) this.getSearchResults(q);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const items = Array.from(this.querySelectorAll('.predictive-search__list-item'));
    if (!items.length) return;
    const current = this.querySelector('[aria-selected="true"]');
    let index = items.indexOf(current);
    index = direction === 'up' ? (index <= 0 ? items.length - 1 : index - 1) : (index + 1) % items.length;
    if (current) current.setAttribute('aria-selected', false);
    items[index].setAttribute('aria-selected', true);
    this.input.setAttribute('aria-activedescendant', items[index].id);
  }

  static normalize(text) {
    return (text || '')
      .toString()
      .toLowerCase()
      .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
      .replace(/[\u0623\u0625\u0622\u0671]/g, '\u0627')
      .replace(/\u0629/g, '\u0647')
      .replace(/\u0649/g, '\u064A')
      .replace(/\u0624/g, '\u0648')
      .replace(/\u0626/g, '\u064A')
      .replace(/[|,.\-_/()]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  static loadIndex() {
    if (PredictiveSearch.indexPromise) return PredictiveSearch.indexPromise;
    const KEY = 'bs_search_index_v1';
    try {
      const cached = JSON.parse(sessionStorage.getItem(KEY) || 'null');
      if (cached && Date.now() - cached.t < 30 * 60 * 1000) {
        PredictiveSearch.indexPromise = Promise.resolve(cached.items);
        return PredictiveSearch.indexPromise;
      }
    } catch (e) {}

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const items = [];
    const loadPage = (page) =>
      fetch(`${root}products.json?limit=250&page=${page}`)
        .then((r) => (r.ok ? r.json() : { products: [] }))
        .then((data) => {
          const products = (data && data.products) || [];
          products.forEach((p) => {
            const variant = (p.variants || [])[0] || {};
            const tags = Array.isArray(p.tags) ? p.tags.join(' ') : p.tags || '';
            items.push({
              title: p.title,
              url: `${root}products/${p.handle}`,
              image: p.images && p.images[0] ? p.images[0].src : '',
              price: variant.price || '',
              compare: variant.compare_at_price || '',
              nt: PredictiveSearch.normalize(p.title),
              n: PredictiveSearch.normalize([p.title, p.product_type, tags, p.handle.replace(/-/g, ' ')].join(' ')),
            });
          });
          if (products.length === 250 && page < 10) return loadPage(page + 1);
        });

    PredictiveSearch.indexPromise = loadPage(1)
      .then(() => {
        try {
          sessionStorage.setItem(KEY, JSON.stringify({ t: Date.now(), items: items }));
        } catch (e) {}
        return items;
      })
      .catch(() => items);
    return PredictiveSearch.indexPromise;
  }

  static escape(s) {
    return (s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  formatMoney(value) {
    const amount = parseFloat(value);
    if (isNaN(amount)) return '';
    const format = window.BS_MONEY_FORMAT || '{{amount}} AED';
    const noDecimals = Math.round(amount).toLocaleString('en-US');
    const withDecimals = amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return format
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*amount_no_decimals[^}]*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withDecimals.replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1'))
      .replace(/\{\{\s*amount[^}]*\}\}/, withDecimals);
  }

  getSearchResults(searchTerm, limit = 10) {
    this.setAttribute('loading', true);
    PredictiveSearch.loadIndex().then((items) => {
      if (searchTerm !== this.getQuery()) return;
      const words = PredictiveSearch.normalize(searchTerm).split(' ').filter(Boolean);
      const matches = items
        .map((item) => {
          if (!words.every((w) => item.n.indexOf(w) !== -1)) return null;
          let score = 0;
          words.forEach((w) => {
            if (item.nt.indexOf(w) !== -1) score += 2;
            if ((' ' + item.nt).indexOf(' ' + w) !== -1) score += 1;
          });
          return { item: item, score: score };
        })
        .filter(Boolean)
        .sort((a, b) => b.score - a.score)
        .slice(0, limit)
        .map((m) => m.item);
      this.renderSearchResults(this.buildMarkup(matches, searchTerm));
    });
  }

  buildMarkup(matches, term) {
    const esc = PredictiveSearch.escape;
    const heading = this.isArabic ? '\u0627\u0644\u0645\u0646\u062a\u062c\u0627\u062a' : 'Products';
    if (!matches.length) {
      const msg = this.isArabic
        ? `\u0644\u0627 \u062a\u0648\u062c\u062f \u0646\u062a\u0627\u0626\u062c \u0644\u0640 "${esc(term)}"`
        : `No results for "${esc(term)}"`;
      return `<div id="predictive-search-results" role="listbox"><p class="predictive-search__heading text-body" style="padding:1.5rem 2rem;margin:0;">${msg}</p></div>`;
    }
    const list = matches
      .map((p, i) => {
        const img = p.image
          ? `<img class="predictive-search__image" src="${esc(p.image)}${p.image.indexOf('?') === -1 ? '?' : '&'}width=150" alt="${esc(p.title)}" width="50" height="50" loading="lazy">`
          : '';
        const compare =
          p.compare && parseFloat(p.compare) > parseFloat(p.price)
            ? `<s class="price-item price-item--regular" style="margin-inline-start:0.6rem;opacity:0.6;">${esc(this.formatMoney(p.compare))}</s>`
            : '';
        return `<li id="predictive-search-option-product-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${esc(p.url)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
            ${img}
            <div class="predictive-search__item-content">
              <p class="predictive-search__item-heading h5">${esc(p.title)}</p>
              <div class="price"><div class="price__regular"><span class="price-item price-item--regular">${esc(this.formatMoney(p.price))}</span>${compare}</div></div>
            </div>
          </a>
        </li>`;
      })
      .join('');
    return `<div id="predictive-search-results" role="listbox">
      <div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
        <div class="predictive-search__result-group"><div>
          <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${heading}</h2>
          <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">${list}</ul>
        </div></div>
      </div>
    </div>`;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.removeAttribute('loading');
    this.open();
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - (document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
